import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Collapse from '@material-ui/core/Collapse'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Typography from '@material-ui/core/Typography'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import ProviderAPI from 'config/api/ProviderAPI'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import { sendEeProviderAlert } from 'config/helpers/integrations-lib'
import PhoneInput from 'components/general/PhoneInput'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import { NotificationManager } from 'react-notifications'
import Loader from 'components/general/Loader'
import MissingInfoDialog from './MissingInfoDialog'

const useStyles = makeStyles(componentStyles)


function PrimaryPersonCard(props) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const api = new ProviderAPI()
    const details = props.details || {}
    const companyDetails = props.companyDetails || {}

    const [showAddPerson, setShowAddPerson] = useState(false)
    const [showAddAdmin, setShowAddAdmin] = useState(false)
    const [newFirstName, setNewFirstName] = useState('')
    const [newLastName, setNewLastName] = useState('')
    const [newEmail, setNewEmail] = useState('')
    const [newPhone, setNewPhone] = useState('')
    const [loading, setLoading] = useState(false)

    // Use for missing fields dialog
    const [showMissingFields, setShowMissingFields] = useState(false)
    const [missingFields, setMissingFields] = useState([])
    // Helper to check if a primary person exists
    const hasPrimaryPerson = details && details.firstName && details.firstName.trim() && details.lastName && details.lastName.trim() && details.email && details.email.trim();

    // Admin-only: the admin's Cognito account status ({ exists, status }). Used
    // to show whether they have logged in yet and to send a login reminder.
    const primaryEmail = (details && details.email) || ''
    const [accountStatus, setAccountStatus] = useState(null)
    const [reminderLoading, setReminderLoading] = useState(false)
    const adminApi = new AdminAPI()

    const accountExists = !!(accountStatus && accountStatus.exists)
    const hasLoggedIn = accountExists && accountStatus.status === 'CONFIRMED'

    useEffect(() => {
        if (!props.admin || !primaryEmail) {
            setAccountStatus(null)
            return
        }
        let active = true
        adminApi.getUserStatus(primaryEmail)
            .then(res => {
                if (active) setAccountStatus(res || null)
            })
            .catch(() => {
                if (active) setAccountStatus(null)
            })
        return () => { active = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primaryEmail, props.admin])

    async function sendLoginReminder() {
        setReminderLoading(true)
        try {
            const res = await adminApi.sendLoginReminder(
                primaryEmail,
                details && details.firstName,
                companyDetails.name
            )
            if (res && res.success) {
                NotificationManager.success('Login reminder sent to ' + primaryEmail)
                if (res.status) setAccountStatus({ exists: true, status: res.status })
            } else {
                NotificationManager.error((res && res.message) || 'Could not send reminder')
                if (res && res.status) setAccountStatus({ exists: true, status: res.status })
            }
        } catch (e) {
            onError(e)
        }
        setReminderLoading(false)
    }

    function getMissingFields() {
        const { firstName, lastName, email, phone } = details || {}
        const fields = [
            { key: 'firstName', label: 'First Name', value: firstName },
            { key: 'lastName', label: 'Last Name', value: lastName },
            { key: 'email', label: 'Email', value: email },
            { key: 'phone', label: 'Phone Number', value: phone }
        ]
        return fields.filter(f => !f.value || f.value.toString().trim() === '')
    }

    async function saveDetails() {
        const missing = getMissingFields()
        if (missing.length > 0) {
            setMissingFields(missing)
            setShowMissingFields(true)
            return
        }
        setLoading(true)
        try {
            const updatedCompany = await api.updatePrimaryPerson(props.companyId, details)
            props.updateCompany(updatedCompany)
            props.updateStage && props.updateStage('companyDetails')
            NotificationManager.success('Details saved')
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    async function saveTeamMember() {
        if (!newFirstName || !newLastName || !newEmail) {
            NotificationManager.error('Please fill all fields')
            return
        }
        if (!companyDetails.url) {
            NotificationManager.error('Company URL is missing — cannot create user account')
            return
        }

        try {
            let email = newEmail.toLowerCase().trim()
            let user = await api.createUserAccount(newFirstName, newLastName, email, companyDetails.name, companyDetails.url)
            const updatedTeam = [...(companyDetails.team || []), {
                firstName: newFirstName,
                lastName: newLastName,
                email: email
            }]
            const updatedCompany = await api.updateTeam(props.companyId, updatedTeam)
            props.updateCompany(updatedCompany)
            setShowAddPerson(false)
            setNewFirstName('')
            setNewLastName('')
            setNewEmail('')
            NotificationManager.success('Team member added')
        } catch (e) {
            onError(e)
        }
    }

    // Save new admin (primary person)
    async function saveAdmin() {
        if (!newFirstName || !newLastName || !newEmail || !newPhone) {
            NotificationManager.error('Please fill all fields')
            return
        }
        if (!companyDetails.url) {
            NotificationManager.error('Company URL is missing — cannot create user account')
            return
        }
        try {
            let email = newEmail.toLowerCase().trim()
            // Create user account for admin
            await api.createUserAccount(newFirstName, newLastName, email, companyDetails.name, companyDetails.url)
            // Update primary person
            const newDetails = {
                firstName: newFirstName,
                lastName: newLastName,
                email: email,
                phone: newPhone
            }
            const updatedCompany = await api.updatePrimaryPerson(props.companyId, newDetails)
            props.updateCompany(updatedCompany)
            sendEeProviderAlert({ type: 'admin_added', company: updatedCompany })
            setShowAddAdmin(false)
            setNewFirstName('')
            setNewLastName('')
            setNewEmail('')
            setNewPhone('')
            NotificationManager.success('Admin added')
        } catch (e) {
            onError(e)
        }
    }

    function checkRequiredFields() {
        const { firstName, lastName, email, phone } = details || {}
        const requiredFields = [firstName, lastName, email, phone]
        return requiredFields.some(field => !field || field.toString().trim() === '')
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <>
                        <Box display='flex' style={{justifyContent: 'space-between'}}>
                            <Box>
                                <Box
                                    component={Typography}
                                    variant='h2'
                                    marginBottom='0!important'
                                >
                                    <Box component='span'>Company Admins</Box>
                                </Box>
                                <Box
                                    component='p'
                                    fontSize='.875rem'
                                    marginBottom='0'
                                    marginTop='0'
                                    lineHeight='1.7'
                                    fontWeight='300'
                                >
                                    Your company admins have full control over your public profile. 
                                </Box>
                            </Box>
                        </Box>
                    </>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            ></CardHeader>
            <Collapse in={open}>
            <CardContent>
                {!hasPrimaryPerson ? (
                    <Grid container>
                        <Grid item xs={12} style={{ textAlign: 'center', margin: '2rem 0' }}>
                            <Typography variant='h5' style={{ marginBottom: 16 }}>
                                No admin has been added for this company
                            </Typography>
                            <Button
                                variant='contained'
                                color='primary'
                                onClick={() => setShowAddAdmin(true)}
                            >
                                Invite Admin Now
                            </Button>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>First Name</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <OutlinedInput
                                        autoComplete='off'
                                        type='text'
                                        placeholder='John'
                                        value={details.firstName}
                                        onChange={(e) => props.updateField('primaryPerson.firstName', e.target.value)}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>Last Name</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <OutlinedInput
                                        autoComplete='off'
                                        type='text'
                                        placeholder='Smith'
                                        value={details.lastName}
                                        onChange={(e) => props.updateField('primaryPerson.lastName', e.target.value)}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>Email</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <OutlinedInput
                                        autoComplete='off'
                                        type='text'
                                        placeholder='john.smith@gmail.com'
                                        value={details.email}
                                        onChange={(e) => props.updateField('primaryPerson.email', e.target.value)}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>Phone Number</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <PhoneInput
                                        variant='outlined'
                                        phone={details?.phone || ''}
                                        updatePhone={(phone) => props.updateField('primaryPerson.phone', phone)}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel>Additional Team Members</FormLabel>
                            {(companyDetails.team || []).length > 0 && (
                                <Box mt={1}>
                                    {(companyDetails.team || []).map((person, index) => (
                                        <Box key={index} mb={1}>
                                            <Typography variant='body2'>
                                                {person.firstName} {person.lastName} ({person.email})
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant='outlined'
                                color='primary'
                                disabled={checkRequiredFields() || loading}
                                onClick={() => setShowAddPerson(true)}
                                style={{ marginRight: '1rem' }}
                            >
                                Add team member
                            </Button>
                            {props.admin && (
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    disabled={reminderLoading || !accountExists || hasLoggedIn}
                                    onClick={() => sendLoginReminder()}
                                    title={hasLoggedIn ? 'This admin has already logged in' : 'Resend login details to this admin'}
                                >
                                    {!reminderLoading ? 'Send Login Reminder' : <Loader height={12} />}
                                </Button>
                            )}
                            {props.admin && primaryEmail && accountStatus && (
                                <Typography
                                    variant='caption'
                                    style={{ display: 'block', marginTop: 8, color: accountExists ? '#8898aa' : '#cc0000' }}
                                >
                                    {!accountExists
                                        ? ('⚠ No login account found for ' + primaryEmail + ' — check the email is correct')
                                        : hasLoggedIn
                                            ? ('This admin has logged in (' + primaryEmail + ')')
                                            : ('This admin has not logged in yet (' + primaryEmail + ')')}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end' width='100%'>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    disabled={loading}
                                    onClick={saveDetails}
                                >
                                    {!loading ? (
                                        <>{(props.admin || props.profile) ? 'Save' : 'Save & Continue'}</>
                                    ) : (
                                        <Loader height={12} />
                                    )}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </CardContent>
            </Collapse>
            {/* Add Admin Dialog */}
            <Dialog open={showAddAdmin} onClose={() => setShowAddAdmin(false)} fullWidth maxWidth='sm'>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                            <Box component={Typography} variant='h3'>
                                <Box component='span'>Add Admin</Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>First Name</FormLabel>
                                <OutlinedInput
                                    value={newFirstName}
                                    onChange={(e) => setNewFirstName(e.target.value)}
                                    fullWidth
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>Last Name</FormLabel>
                                <OutlinedInput
                                    value={newLastName}
                                    onChange={(e) => setNewLastName(e.target.value)}
                                    fullWidth
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>Email</FormLabel>
                                <OutlinedInput
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    fullWidth
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>Phone Number</FormLabel>
                                <PhoneInput
                                    variant='outlined'
                                    phone={newPhone}
                                    updatePhone={(phone) => setNewPhone(phone)}
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <p>A temporary password and a confirmation code and will be sent to the email above, this is required to confirm the new user's email when they first login</p>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end' p={2}>
                                <Button color='primary' variant='outlined' onClick={() => setShowAddAdmin(false)} style={{ marginRight: '1rem' }}>Cancel</Button>
                                <Button color='primary' variant='contained' onClick={saveAdmin}>Add Admin</Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            <Dialog open={showAddPerson} onClose={() => setShowAddPerson(false)} fullWidth maxWidth='sm'>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                            <Box component={Typography} variant='h3'>
                                <Box component='span'>Add Team Member</Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>First Name</FormLabel>
                                <OutlinedInput
                                    value={newFirstName}
                                    onChange={(e) => setNewFirstName(e.target.value)}
                                    fullWidth
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormGroup>
                                <FormLabel>Last Name</FormLabel>
                                <OutlinedInput
                                    value={newLastName}
                                    onChange={(e) => setNewLastName(e.target.value)}
                                    fullWidth
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <FormGroup>
                                <FormLabel>Email</FormLabel>
                                <OutlinedInput
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    fullWidth
                                />
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <p>A temporary password and a confirmation code and will be sent to the email above, this is required to confirm the new user's email when they first login</p>
                        </Grid>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end' p={2}>
                                <Button color='primary' variant='outlined' onClick={() => setShowAddPerson(false)} style={{ marginRight: '1rem' }}>Cancel</Button>
                                <Button color='primary' variant='contained' onClick={saveTeamMember}>Send Invite</Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            {/* Use the new MissingInfoDialog component */}
            <MissingInfoDialog
                open={showMissingFields}
                onClose={() => setShowMissingFields(false)}
                missingFields={missingFields}
            />
        </Card>
    );
}

export default PrimaryPersonCard